import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { emitVisitCompleteWhenPhotosSettle, emitVisitError } from '../lib/embedBridge';
import ConciergeOtoGate from './intake/ConciergeOtoGate';
import DexaCreditsNote from './intake/DexaCreditsNote';
import { useHandoffSeed, skipHandoffContact } from '@/lib/handoff';
import { usePreview, PreviewInterstitial } from './preview/PreviewShell';
import QuestionStep from './intake/QuestionStep';
import MultiSelectStep from './intake/MultiSelectStep';
import InputStep from './intake/InputStep';
import HeightWeightBmiStep from './intake/HeightWeightBmiStep';
import TextareaStep from './intake/TextareaStep';
import DisqualifiedStep from './intake/DisqualifiedStep';
import BasicContactStep from './intake/BasicContactStep';
import ShippingAddressStep from './intake/ShippingAddressStep';
import ConsentDropdownStep from './intake/ConsentDropdownStep';
import FileUploadStep from './intake/FileUploadStep';
import Footer from './intake/Footer';
import AccountSetupCta from './intake/AccountSetupCta';
import CatalogCheckoutStep from './intake/CatalogCheckoutStep';
import BrandLogo from '@/components/BrandLogo';
import { buildTenantUrl } from '@/lib/tenant';
import {
  buildDynamicWomenSexualHealthFlow,
  isDisqualifyingAnswer,
} from '../data/womenSexualHealthData';
import { apiService } from '../services/api';
import {
  consentAnswerLabel,
  composeQaQuestionText,
  describeSubmitFailure,
  formatMultiSelectAnswer,
  splitHeightWeightAnswer,
} from '../lib/utils';
import { MutatingDots } from 'react-loader-spinner';

const STORAGE_KEY = 'fitpal_wsh_progress';

// Resume modal — identical UX to the other intake forms.
const ResumeModal = ({ onResume, onStartFresh }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Welcome back!</h3>
        <p className="text-gray-500">
          We found your previous progress. Would you like to continue where you left off?
        </p>
      </div>
      <div className="space-y-3">
        <button onClick={onResume} className="btn-primary w-full" data-testid="resume-progress-btn">
          Continue where I left off
        </button>
        <button
          onClick={onStartFresh}
          className="w-full py-3 px-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
          data-testid="start-fresh-btn"
        >
          Start fresh
        </button>
      </div>
    </div>
  </div>
);

const WomenSexualHealthForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  // §authenticated-patient handoff: prefill contact from the resolved ?pt= patient.
  useHandoffSeed(setAnswers);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [slideDirection, setSlideDirection] = useState('initial');
  const [slideKey, setSlideKey] = useState(0);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [visitResult, setVisitResult] = useState(null);

  // Synchronous re-entry guard for checkout submission (mirrors TrtForm) — the
  // Square widget / a fast double-click can fire onComplete twice within one
  // React tick, producing duplicate /beluga/create-visit-v2 POSTs.
  const submittingRef = useRef(false);

  // Auto-advance single-fire guard.
  const advancedForSlideKeyRef = useRef(null);
  const advanceTimerRef = useRef(null);
  useEffect(
    () => () => {
      if (advanceTimerRef.current !== null) clearTimeout(advanceTimerRef.current);
    },
    [],
  );

  // Load saved progress on mount.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.currentStep > 0) {
          setSavedProgress(parsed);
          setShowResumeModal(true);
        }
      }
    } catch (e) {
      console.error('Error loading saved progress:', e);
    }
  }, []);

  // Persist progress.
  useEffect(() => {
    if (currentStep > 0 && !isComplete && !isDisqualified) {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ currentStep, answers, timestamp: Date.now() }),
        );
      } catch (e) {
        console.error('Error saving progress:', e);
      }
    }
  }, [currentStep, answers, isComplete, isDisqualified]);

  const clearSavedProgress = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing progress:', e);
    }
  }, []);

  const handleResumeProgress = useCallback(() => {
    if (savedProgress) {
      setCurrentStep(savedProgress.currentStep);
      setAnswers(savedProgress.answers || {});
      setShowResumeModal(false);
    }
  }, [savedProgress]);

  const handleStartFresh = useCallback(() => {
    clearSavedProgress();
    setShowResumeModal(false);
    setSavedProgress(null);
  }, [clearSavedProgress]);

  const preview = usePreview();
  const currentFlow = useMemo(() => preview.wrapFlow(skipHandoffContact(buildDynamicWomenSexualHealthFlow(answers))), [answers, preview]);
  const totalSteps = currentFlow.length;
  const currentQuestion = currentFlow[currentStep];

  const handleDisqualify = useCallback(
    (reason) => {
      setIsDisqualified(true);
      setDisqualifyReason(reason);
      clearSavedProgress();
      setSlideDirection('forward');
      setSlideKey((prev) => prev + 1);
    },
    [clearSavedProgress],
  );

  const handleAnswer = useCallback(
    (questionId, answer) => {
      const previousAnswer = answers[questionId];
      setAnswers((prev) => ({ ...prev, [questionId]: answer }));

      const disqualResult = isDisqualifyingAnswer(questionId, answer);
      if (disqualResult.disqualified) {
        handleDisqualify(disqualResult.reason);
        return;
      }

      // Auto-advance single-select — single-fire per slideKey.
      if (currentQuestion?.type === 'single-select' && answer !== previousAnswer) {
        if (advancedForSlideKeyRef.current === slideKey) return;
        advancedForSlideKeyRef.current = slideKey;
        if (advanceTimerRef.current !== null) clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = setTimeout(() => {
          advanceTimerRef.current = null;
          setCurrentStep((prev) => {
            if (prev >= totalSteps - 1) return prev;
            setSlideDirection('forward');
            setSlideKey((k) => k + 1);
            return prev + 1;
          });
        }, 200);
      }
    },
    [totalSteps, currentQuestion, answers, handleDisqualify, slideKey],
  );

  const handleNext = useCallback(() => {
    if (currentQuestion) {
      const currentAnswer = answers[currentQuestion.id];
      const disqualResult = isDisqualifyingAnswer(currentQuestion.id, currentAnswer);
      if (disqualResult.disqualified) {
        handleDisqualify(disqualResult.reason);
        return;
      }
    }
    if (currentStep < totalSteps - 1) {
      setSlideDirection('forward');
      setSlideKey((prev) => prev + 1);
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, totalSteps, currentQuestion, answers, handleDisqualify]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setSlideDirection('backward');
      setSlideKey((prev) => prev + 1);
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const handleStartOver = useCallback(() => {
    setSlideDirection('backward');
    setSlideKey((prev) => prev + 1);
    setCurrentStep(0);
    setAnswers({});
    setIsComplete(false);
    setIsDisqualified(false);
    setDisqualifyReason('');
    clearSavedProgress();
  }, [clearSavedProgress]);

  const getAnimationClass = () => {
    switch (slideDirection) {
      case 'forward':
        return 'slide-forward';
      case 'backward':
        return 'slide-backward';
      default:
        return 'slide-initial';
    }
  };

  const renderStep = () => {
    if (isDisqualified) {
      return (
        <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
          <DisqualifiedStep reason={disqualifyReason} onStartOver={handleStartOver} />
        </div>
      );
    }

    if (!currentQuestion) return null;

    const commonProps = {
      question: preview.withIcons(currentQuestion),
      value: answers[currentQuestion.id],
      onAnswer: (answer) => handleAnswer(currentQuestion.id, answer),
      onNext: handleNext,
      onBack: handleBack,
      canGoBack: currentStep > 0,
      isLastStep: currentStep === totalSteps - 1,
      setLoading,
    };

    const stepContent = (() => {
      switch (currentQuestion.type) {
        case 'single-select':
          return <QuestionStep {...commonProps} />;
        case 'multi-select':
          return <MultiSelectStep {...commonProps} />;
        case 'input':
        case 'number':
        case 'date':
        case 'text':
          return <InputStep {...commonProps} />;
        case 'textarea':
          return <TextareaStep {...commonProps} />;
        case 'height-weight-bmi':
          return (
            <HeightWeightBmiStep
              {...commonProps}
              onDisqualify={handleDisqualify}
              isOnTreatment={false}
            />
          );
        case 'consent-dropdown':
          return <ConsentDropdownStep {...commonProps} onDisqualify={handleDisqualify} />;
        case 'file-upload':
          return <FileUploadStep {...commonProps} />;
        case 'basic-contact':
          return <BasicContactStep {...commonProps} />;
        case 'shipping-address':
          return <ShippingAddressStep {...commonProps} />;
        case 'checkout':
          return (
            <CatalogCheckoutStep
              catalogVisitType="mensSexualHealth"
              belugaVisitType="womenSexualHealth"
              filter={(i) => /pt-?141|bremelanotide|oxytocin/i.test((i.name || '') + (i.strength || ''))}
              onComplete={async (payment) => {
                if (submittingRef.current) return;
                submittingRef.current = true;
                try {
                  setIsSubmitting(true);
                  setAnswers((prev) => ({ ...prev, payment }));

                  // Build the answers[] array Beluga's relayer expects.
                  const formattedAnswers = Object.entries(answers).flatMap(
                    ([questionId, answer]) => {
                      const split = splitHeightWeightAnswer(answer);
                      if (split) return split;
                      const slide = currentFlow.find((q) => q.id === questionId);
                      // '; ' so the backend can bullet multi-selects even when an
                      // option label contains ', ' (count-validated split, eb31b20).
                      const consentLabel = consentAnswerLabel(slide, answer, '; ');
                      let formattedAnswer;
                      if (consentLabel !== null) {
                        formattedAnswer = consentLabel;
                      } else if (Array.isArray(answer)) {
                        formattedAnswer = formatMultiSelectAnswer(answer);
                      } else if (typeof answer === 'object' && answer !== null) {
                        if (answer.preview) {
                          formattedAnswer = `File: ${answer.fileName || 'uploaded'}`;
                        } else {
                          formattedAnswer = JSON.stringify(answer);
                        }
                      } else {
                        formattedAnswer = String(answer);
                      }
                      const questionText = composeQaQuestionText(slide) || questionId;
                      const record = {
                        question: questionText,
                        answer: formattedAnswer,
                        slide_id: questionId,
                        rawAnswer: answer,
                      };
                      if (
                        slide &&
                        typeof slide.consentText === 'string' &&
                        slide.consentText.trim()
                      ) {
                        record.consentBody = slide.consentText;
                      }
                      return [record];
                    },
                  );

                  const contactInfo = answers['basic-contact'] || {};
                  const shipping = answers['shipping-address'] || {};
                  const paymentId =
                    payment?.paymentId ||
                    payment?.payment_id ||
                    localStorage.getItem('fitpal_intake_payment_id') ||
                    '';

                  // §named-field: sex-at-birth maps to the `sex` named field —
                  // surface it on the contact envelope ('female' → 'Female').
                  const sexRaw = answers['sex-at-birth'];
                  const sex = sexRaw
                    ? sexRaw.charAt(0).toUpperCase() + sexRaw.slice(1)
                    : '';

                  const selectedPlans = Array.isArray(payment?.selectedPlans)
                    ? payment.selectedPlans.filter(Boolean)
                    : [];

                  const result = await apiService.submitForm(
                    formattedAnswers,
                    {
                      first_name: contactInfo.name?.split(' ')[0] || '',
                      last_name: contactInfo.name?.split(' ').slice(1).join(' ') || '',
                      email: contactInfo.email || '',
                      phone: contactInfo.phone || '',
                      dob: contactInfo.dob || '',
                      address: shipping.address1 || '',
                      address2: shipping.address2 || '',
                      city: shipping.city || '',
                      state: shipping.state || '',
                      zip: shipping.zipCode || '',
                      sex,
                    },
                    paymentId,
                    'womenSexualHealth',
                    'womenSexualHealth',
                    {
                      planId: selectedPlans[0] || null,
                      selectedPlans,
                      // §catalog: forward the exact catalog selection so the BE
                      // builds patientPreference from these medIds. Catalog is
                      // fetched from mensSexualHealth (women's products live
                      // there) but the Beluga visitType stays womenSexualHealth.
                      selectedMedIds:
                        (Array.isArray(payment?.selectedMedIds) && payment.selectedMedIds.length)
                          ? payment.selectedMedIds
                          : undefined,
                      // §fulfillment: forward the patient's ship-vs-local choice
                      // and the chosen pharmacy object from CatalogCheckoutStep.
                      prescriptionFulfillment: payment?.prescriptionFulfillment,
                      // §dexa-add-on: forward what the patient actually bought.
                      // Without this the add-on is CHARGED (SquarePaymentForm gets
                      // amount=totalCents, which includes it) while the record of
                      // what it was for never leaves the browser.
                      dexaAddOn: payment?.dexaAddOn,
                      pharmacySelection: payment?.pharmacySelection,
                    },
                  );

                  // Identity photo relay (same pattern as the other flows).
                  const idPhoto = answers['identity-verification'];
                  if (idPhoto && idPhoto.preview && result?.masterId) {
                    apiService
                      .uploadVisitPhoto({
                        masterId: result.masterId,
                        visitId: result.visitId,
                        slideId: 'identity-verification',
                        photo: idPhoto,
                      })
                      .catch((err) => {
                        console.warn(
                          '[uploadVisitPhoto] identity-verification relay failed:',
                          err?.response?.data || err.message,
                        );
                      });
                  }

                  setVisitResult(result);

                  // §embed-bridge: tell the dashboard what was created. Waits for any

                  // in-flight photo relay first — the parent closes its modal on this

                  // message, which unmounts the iframe and would abort the upload.

                  emitVisitCompleteWhenPhotosSettle({

                    masterId: result?.masterId,

                    visitId: result?.visitId,

                    scheduleLink: result?.scheduleLink,

                  });
                  setIsComplete(true);
                  handleNext();
                  clearSavedProgress();
                } catch (error) {
                  console.error('Women\'s sexual health visit creation error:', error);
                  // §embed-bridge: relay the RAW backend error — the dashboard reads
                  // errorCode/refundId to tell the patient their card was refunded.
                  emitVisitError(error);
                  alert(describeSubmitFailure(error));
                } finally {
                  submittingRef.current = false;
                  setIsSubmitting(false);
                }
              }}
              isProcessing={isSubmitting}
              answers={answers}
              setLoading={setLoading}
              clearSavedProgress={clearSavedProgress}
              primaryGoal={answers['primary-goal']}
              currentStep={currentStep}
              timestamp={Date.now()}
              email={answers['basic-contact']?.email}
            />
          );
        case 'complete':
          return (
            <ConciergeOtoGate answers={answers} visitType="womenSexualHealth" visitResult={visitResult}>
            <div className="max-w-xl mx-auto text-center py-10" data-testid="wsh-complete-step">
              <div className="w-16 h-16 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-5">
                <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Thanks{answers['basic-contact']?.name ? `, ${answers['basic-contact'].name.split(' ')[0]}` : ''}!
              </h2>
              <p className="text-gray-500 mb-2">
                Your intake has been submitted. A licensed provider will review your
                responses and follow up with next steps.
              </p>
              {/* §preference-not-prescription: the product picked at checkout is a
                  request; the provider decides what is actually prescribed. */}
              <p className="text-sm text-gray-400 mb-4">
                Your provider will prescribe the treatment that&rsquo;s right for you,
                which may differ from the option you selected.
              </p>
              {visitResult?.masterId && (
                <div className="inline-flex flex-col items-center bg-gray-50 rounded-xl px-6 py-3">
                  <span className="text-xs uppercase tracking-wide text-gray-400">Visit reference</span>
                  <span className="text-lg font-semibold text-gray-900" data-testid="wsh-visit-ref">
                    {visitResult.masterId}
                  </span>
                </div>
              )}
              <AccountSetupCta email={answers['basic-contact']?.email} />
              <DexaCreditsNote answers={answers} />
            </div>
            </ConciergeOtoGate>
          );
        case 'preview-interstitial':
          return <PreviewInterstitial step={currentQuestion} onNext={handleNext} />;
        default:
          return <QuestionStep {...commonProps} />;
      }
    })();

    return (
      <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
        {preview.frame(currentQuestion, stepContent, handleBack)}
      </div>
    );
  };

  return (
    <>
      <div className="intake-form-loader" style={{ display: loading ? '' : 'none' }}>
        <MutatingDots />
      </div>

      <div
        className={`min-h-screen flex flex-col ${preview.rootClass}`}
        data-testid="wsh-form"
        style={{ display: loading === false ? '' : 'none' }}
      >
        {showResumeModal && (
          <ResumeModal onResume={handleResumeProgress} onStartFresh={handleStartFresh} />
        )}

        <header className="w-full py-4 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            {currentStep > 0 && !isComplete && !isDisqualified ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
                data-testid="back-button"
                style={{ display: currentQuestion?.type === 'complete' ? 'none' : '' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">Back</span>
              </button>
            ) : (
              <a href={buildTenantUrl('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0" data-testid="home-link">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">Home</span>
              </a>
            )}

            <div className="text-center flex-1">
              <BrandLogo
                alt="FitPal"
                className="mx-auto"
                style={{ width: '400px', height: 'auto', maxWidth: '100%' }}
              />
              <p className="text-xs text-gray-500 mt-1">Women's Sexual Health Program</p>
            </div>

            <div className="w-16 flex-shrink-0" />
          </div>
        </header>

        {!isComplete && !isDisqualified && totalSteps > 0 && (
          <div className="w-full px-4 py-2">
            <div className="max-w-2xl mx-auto">
              {preview.progress(currentQuestion, currentStep, totalSteps)}
            </div>
          </div>
        )}

        <main className="flex-1 flex flex-col items-center justify-start px-4 py-8">
          <div
            className={`w-full ${
              currentQuestion && currentQuestion.type === 'checkout' ? 'max-w-5xl' : 'max-w-xl'
            } slide-container`}
          >
            {renderStep()}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default WomenSexualHealthForm;
